/**
 * Static registry of game modules, keyed by slug.
 *
 * Why static (not dynamic discovery): keeps the bundle splittable and the
 * route → module mapping explicit. Every game is a lazy import so adding a
 * game costs zero bytes for residents who don't open it.
 *
 * Games implement the canonical {@link GameModule} from `@rendever/games-sdk`.
 */
import type { GameModule } from '@rendever/games-sdk';

export type { GameModule };

/**
 * Slug → dynamic importer. Returning `null` from an importer signals
 * "registered but not yet implemented" — `GameHostPage` shows a calm
 * placeholder rather than crashing.
 *
 * `word-search` loads the themedshape variant's bespoke renderer so the
 * `/play/word-search?category=<slug>` deep links from the themedshape Hub
 * land on the variant's atmospheric grid rather than the legacy renderer.
 */
export const GAME_REGISTRY: Record<string, () => Promise<GameModule | null>> = {
  trivia: async () => (await import('./trivia')).TriviaGameModule,
  five: async () => (await import('./five')).FiveGameModule,
  crossword: async () => (await import('./crossword')).CrosswordGameModule,
  mahjong: async () => (await import('./mahjong')).MahjongGameModule,
  atlas: async () => (await import('./atlas')).AtlasGameModule,
  bingo: async () => (await import('./bingo')).BingoGameModule,
  sudoku: async () => (await import('./sudoku')).SudokuGameModule,
  solitaire: async () => (await import('./solitaire')).SolitaireGameModule,
  'word-search': async () =>
    (await import('@/variants/wordsearch-themedshape')).WordSearch,
  balloons: async () => (await import('./balloons')).BalloonsGameModule,
  categories: async () => (await import('./categories')).CategoriesGameModule,
  wheel: async () => (await import('./the-wheel')).WheelGameModule,
};

/**
 * Games shown as primary tiles on the Reading Room Hub. Word Search remains
 * in the registry above so its route keeps working, but it is relegated to a
 * "More coming" footer in the Hub until it reaches the same craft bar as
 * Trivia.
 */
export const HUB_PRIMARY_SLUGS = ['trivia'] as const;
export const HUB_FOOTER_SLUGS = ['word-search'] as const;

export function hasGame(slug: string): boolean {
  return slug in GAME_REGISTRY;
}

export async function loadGame(slug: string): Promise<GameModule | null> {
  const loader = GAME_REGISTRY[slug];
  if (!loader) return null;
  try {
    return await loader();
  } catch (err) {
    console.error(`[games] failed to load "${slug}"`, err);
    return null;
  }
}

/** All registered slugs — useful for the Hub picker. */
export function listGames(): string[] {
  return Object.keys(GAME_REGISTRY);
}
